<script>
import Layout from '@layouts/main.vue'
import {
  eventMethods,
  authComputed,
  cityMethods,
  cityComputed,
  purposeMethods,
  purposeComputed,
  groupMethods,
  groupComputed,
} from '@state/helpers'
import dayjs from 'dayjs'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import 'vue2-datepicker/locale/lt'

export default {
  page() {
    return {
      title: this.isEdit ? this.name : 'Sukurti kalendoriaus įvykį',
    }
  },

  components: { Layout, DatePicker },

  data() {
    return {
      form: {
        title: '',
        description: '',
        date: dayjs().format('YYYY-MM-DD'),
        time: '',
        duration: '',
        city: '',
        place: '',
        purpose: '',
        invite_active: false,
        invite_inactive: false,
        invite_coordinators: false,
        invite_admins: false,
        invite_nv: true,
        invite_kc: false,
        invite_drauge: false,
        invite_grupese: false,
        invite_groups: [],
        future_event: false,
      },
      errorMessage: null,
      isLoading: false,
      failedField: null,
    }
  },

  computed: {
    ...authComputed,

    ...cityComputed,

    ...purposeComputed,

    ...groupComputed,

    eventId() {
      return this.$route.params.id
    },

    isEdit() {
      return !!this.eventId
    },

    isFutureEvent() {
      return !this.isEdit || this.form.future_event
    },
  },

  created() {
    this.fetchAllGroups()
    this.fetchAllPurposes()
    this.fetchAllCities()
    this.setFilterCity()
    this.fetchUser()
    this.loadDefaultDate()
  },

  methods: {
    ...eventMethods,

    ...cityMethods,

    ...purposeMethods,

    ...groupMethods,

    setFilterCity() {
      this.form.city = this.currentUser.city
    },

    fetchUser() {
      if (this.isEdit) {
        this.viewEvent(this.eventId)
          .then(this.updateEventForm)
          .catch(() => this.$router.push({ name: 'calendar' }))
      }
    },

    loadDefaultDate() {
      if (this.$route.query.date) {
        this.form.date = this.$route.query.date
      }
    },

    updateEventForm(event) {
      Object.keys(this.form).forEach((field) => {
        if (event[field]) {
          this.form[field] = event[field]
        }
      })
    },

    onSubmitForm() {
      this.resetFormState()

      const action = this.isEdit ? this.updateEvent : this.createEvent

      return action({
        id: this.eventId,
        ...this.form,
      })
        .then((token) => {
          this.resetFormState()
          this.$router.push({ name: 'calendar' })
        })
        .catch((error) => {
          this.isLoading = false
          this.errorMessage = 'Formoje yra klaidų.'
          if (
            error &&
            error.response &&
            error.response.data &&
            error.response.data.error
          ) {
            this.failedField = error.response.data.error.replace(/.*: /, '')
          }
        })
    },

    resetFormState() {
      this.isLoading = true
      this.errorMessage = null
      this.failedField = null
    },

    onClickDeleteEvent() {
      if (confirm('Ar tikrai norite ištrinti įrašą?')) {
        this.deleteEvent(this.eventId).then(() =>
          this.$router.push({ name: 'calendar' }),
        )
      }
    },

    onChangeAddOrRemoveGroup(group) {
      if (this.form.invite_groups.includes(group.label)) {
        this.form.invite_groups.pop(group.label)
      } else {
        this.form.invite_groups.push(group.label)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold">
          <template v-if="isEdit">{{ form.title }}</template>
          <template v-else> Sukurti įvykį </template>
        </h1>
      </template>
      <template v-slot:actions>
        <div>
          <base-btn @click="$router.push({ name: 'calendar' })">
            <font-awesome-icon :icon="['fas', 'undo']" class="mr-1" />
            Grįžti atgal
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <p v-if="errorMessage" class="text-error mb-4">
      {{ errorMessage }}
    </p>

    <form @submit.prevent="onSubmitForm">
      <div class="mb-4">
        <base-label-input
          v-model="form.title"
          type="text"
          required
          autofocus
          :error="failedField === 'title'"
          label="Pavadinimas"
        />
      </div>

      <div class="mb-4">
        <base-label-textarea
          v-model="form.description"
          :error="failedField === 'description'"
          label="Aprašymas"
        />
      </div>

      <div class="flex flex-col lg:flex-row">
        <div class="flex-1 mb-4">
          <base-label-text label="Data" class="datepicker">
            <DatePicker
              v-model="form.date"
              value-type="format"
              format="YYYY-MM-DD"
            />
          </base-label-text>
        </div>
        <div class="flex-1 lg:ml-4 mb-4">
          <base-label-input
            v-model="form.time"
            type="text"
            :error="failedField === 'time'"
            label="Laikas"
          />
        </div>
        <div class="flex-1 lg:ml-4 mb-4">
          <base-label-input
            v-model="form.duration"
            type="text"
            :error="failedField === 'duration'"
            label="Trukmė, val."
          />
        </div>
      </div>

      <div class="flex flex-row">
        <div class="mb-4 flex-1 pr-4">
          <base-label-text v-if="currentUser.role === 'admin'" label="Miestas">
            <base-select-input
              v-model="form.city"
              :blank-option="{ id: '', label: '' }"
              :options="cityOptions || []"
              :error="failedField === 'city'"
            />
          </base-label-text>
          <base-label-text v-else label="Miestas" required>
            <base-input
              v-model="form.city"
              :blank-option="{ id: '', label: '' }"
              :options="cityOptions || []"
              class="w-full input rounded"
              readonly
              :error="failedField === 'city'"
            />
          </base-label-text>
        </div>
        <div class="flex-1">
          <base-label-input
            v-model="form.place"
            type="text"
            :error="failedField === 'place'"
            label="Vieta"
          />
        </div>
      </div>

      <div class="mb-4">
        <base-label-text
          v-if="currentUser.role === 'admin'"
          label="Renginio tipas"
        >
          <base-select-input
            v-model="form.purpose"
            :blank-option="{ id: '', label: '' }"
            :options="purposeOptions || []"
            :error="failedField === 'purpose'"
          />
        </base-label-text>
        <base-label-text v-else label="Renginio tipas">
          <base-input
            v-model="form.purpose"
            :blank-option="{ id: '', label: '' }"
            :options="purposeOptions || []"
            class="w-full input rounded"
            readonly
            :error="failedField === 'purpose'"
          />
        </base-label-text>
      </div>

      <base-label-text label="Prieinama programoms" class="mb-4" />

      <div class="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        <div>
          <base-label-check
            type="text"
            label="NV programos dalyviams"
            :selected="form.invite_nv"
            :value="form.invite_nv"
            @change="form.invite_nv = !form.invite_nv"
          />
        </div>

        <div>
          <base-label-check
            type="text"
            label="KC programos dalyviams"
            :selected="form.invite_kc"
            :value="form.invite_kc"
            @change="form.invite_kc = !form.invite_kc"
          />
        </div>

        <div>
          <base-label-check
            type="text"
            label="Drauge programos dalyviams"
            :selected="form.invite_drauge"
            :value="form.invite_drauge"
            @change="form.invite_drauge = !form.invite_drauge"
          />
        </div>

        <div>
          <base-label-check
            type="text"
            label="Grupių programos dalyviams"
            :selected="form.invite_grupese"
            :value="form.invite_grupese"
            @change="form.invite_grupese = !form.invite_grupese"
          />
        </div>
      </div>

      <base-label-text
        v-if="isFutureEvent"
        label="Išsiųsti pakvietimus į renginį"
        class="mb-4"
      />

      <div
        v-if="isFutureEvent"
        class="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-4"
      >
        <div>
          <base-label-check
            v-model="form.invite_active"
            type="text"
            :error="failedField === 'invite_active'"
            label="Dirbantiems savanoriams"
          />
        </div>

        <div>
          <base-label-check
            v-model="form.invite_inactive"
            type="text"
            :error="failedField === 'invite_inactive'"
            label="Nedirbantiems savanoriams"
          />
        </div>

        <div>
          <base-label-check
            v-model="form.invite_coordinators"
            type="text"
            :error="failedField === 'invite_coordinators'"
            label="Koordinatoriams"
          />
        </div>

        <div>
          <base-label-check
            v-model="form.invite_admins"
            type="text"
            :error="failedField === 'invite_admins'"
            label="Administratoriams"
          />
        </div>

        <div v-for="group of groupOptions" :key="group.title">
          <base-label-check
            type="text"
            :label="`&quot;${group.label}&quot; savanoriams`"
            @change="onChangeAddOrRemoveGroup(group)"
          />
        </div>
      </div>

      <div class="mb-4 flex flex-col lg:flex-row">
        <div class="w-full lg:w-64 mb-4">
          <base-submit :loading="isLoading" class="p-4">
            {{ isEdit ? 'Išsaugoti pakeitimus' : 'Sukurti įvykį' }}
          </base-submit>
        </div>
        <div class="w-full lg:w-40 lg:pl-4">
          <base-btn-text
            v-if="isEdit"
            class="text-error w-full p-4"
            @click="onClickDeleteEvent"
          >
            Pašalinti
          </base-btn-text>
        </div>
      </div>
    </form>
  </Layout>
</template>
